import { CapitalActionNoticeStatus } from 'further-types/lp/capital-action-notice';
import { ApiInputs } from 'lib/trpcClient';
import {
  CreateNoticeSchemaFormValues,
  FullyValidatedCreateNoticeSchemaFormValues,
} from './schema';
import { CapitalActionNoticeCreatorData } from './useCapitalActionNotice/types';

export const mapCapitalActionNoticeCreatorDataToFormValues = (
  data: CapitalActionNoticeCreatorData,
): CreateNoticeSchemaFormValues => {
  return {
    generalInformation: {
      noticeDate: data.noticeDate,
      mainContactEmails: data.mainContactEmails ?? '',
      lcyRecipientAccountId: data.lcyRecipientBankAccountId,
      capitalActionDueDate: data.capitalActionDueDate,
      capitalActionName: data.capitalActionName,
    },
    useOfFunds: {
      totalCallValueLcy: data.generalCapitalBreakdown?.valueLcy,
      investments: {
        percentage: data.generalCapitalBreakdown?.investmentsPercentage,
        valueLcy: data.generalCapitalBreakdown?.investmentsAmount,
      },
      managementFees: {
        percentage: data.generalCapitalBreakdown?.managementFeesPercentage,
        valueLcy: data.generalCapitalBreakdown?.managementFeesAmount,
      },
      otherExpenses: {
        percentage: data.generalCapitalBreakdown?.otherExpensesPercentage,
        valueLcy: data.generalCapitalBreakdown?.otherExpensesAmount,
      },
    },
    noticeNarrative: {
      noticeNarrative: data.noticeNarrative ?? '',
    },
    notificationPreferences: {
      attachPdfNoticeToEmail:
        data.notificationPreferences.attachPdfNoticeToEmail,
      automaticallySendReminders:
        data.notificationPreferences.sendReminderEmails,
      notifyInvestorsViaEmail: data.notificationPreferences.notifyViaEmail,
    },
    perInvestorBreakdown: data.perInvestorBreakdown.map((entry) => {
      const settingsForInvestor = data.perInvestorNotificationSettings.find(
        (settings) => settings.investorId === entry.id,
      );
      return {
        id: entry.id,
        investorName: entry.investorName,
        capitalCalledLcy: entry.capitalCalledLcy,
        capitalDistributedLcy: entry.capitalDistributedLcy,
        totalDueLcy: entry.totalDueLcy,
        unfundedCommitmentLcy: entry.unfundedCommitmentLcy,
        unfundedCommitmentPercentage: entry.unfundedCommitmentPercentage,
        notifyViaEmail: settingsForInvestor?.notifyViaEmail,
        sendReminderEmails: settingsForInvestor?.sendReminderEmails,
      };
    }),
    useFullValidation: false,
    resendPublishedNotices: false,
  };
};

export const mapFormValuesToSaveMutationInput = (
  input: Omit<CreateNoticeSchemaFormValues, 'useFullValidation'> & {
    id: string;
    capitalActionId: string;
    vehicleId: string;
    status: CapitalActionNoticeStatus;
  },
): ApiInputs['capitalActionNotice']['update'] => {
  return {
    id: input.id,
    vehicleId: input.vehicleId,
    capitalActionId: input.capitalActionId,
    capitalActionName: input.generalInformation.capitalActionName,
    noticeDate: input.generalInformation.noticeDate ?? undefined,
    mainContactEmails: input.generalInformation.mainContactEmails,
    lcyRecipientBankAccountId:
      input.generalInformation.lcyRecipientAccountId ?? undefined,
    status: input.status,
    capitalActionDueDate:
      input.generalInformation.capitalActionDueDate ?? undefined,
    noticeNarrative: input.noticeNarrative.noticeNarrative,
    generalCapitalBreakdown: {
      investmentsAmount: input.useOfFunds.investments.valueLcy,
      investmentsPercentage: input.useOfFunds.investments.percentage,
      managementFeesAmount: input.useOfFunds.managementFees.valueLcy,
      managementFeesPercentage: input.useOfFunds.managementFees.percentage,
      otherExpensesAmount: input.useOfFunds.otherExpenses.valueLcy,
      otherExpensesPercentage: input.useOfFunds.otherExpenses.percentage,
    },
    notificationPreferences: {
      notifyViaEmail: input.notificationPreferences.notifyInvestorsViaEmail,
      attachPdfNoticeToEmail:
        input.notificationPreferences.attachPdfNoticeToEmail,
      sendReminderEmails:
        input.notificationPreferences.automaticallySendReminders,
    },
    perInvestorNotificationSettings: input.perInvestorBreakdown.map(
      (entry) => ({
        investorId: entry.id,
        notifyViaEmail: entry.notifyViaEmail,
        sendReminderEmails: false,
      }),
    ),
    resendPublishedNotices: input.resendPublishedNotices,
  };
};

export const mapFullyValidatedFormValuesToSaveMutationInput = (
  input: Omit<
    FullyValidatedCreateNoticeSchemaFormValues,
    'useFullValidation'
  > & {
    id: string;
    capitalActionId: string;
    vehicleId: string;
    status: CapitalActionNoticeStatus;
  },
): ApiInputs['capitalActionNotice']['publish'] => {
  const limitedValidationValues = mapFormValuesToSaveMutationInput(input);
  return {
    ...limitedValidationValues,
    noticeNarrative: input.noticeNarrative.noticeNarrative,
    capitalActionDueDate: input.generalInformation.capitalActionDueDate,
    lcyRecipientBankAccountId: input.generalInformation.lcyRecipientAccountId,
    noticeDate: input.generalInformation.noticeDate,
    mainContactEmails: input.generalInformation.mainContactEmails,
    notificationPreferences: {
      notifyViaEmail: input.notificationPreferences.notifyInvestorsViaEmail,
      attachPdfNoticeToEmail:
        input.notificationPreferences.attachPdfNoticeToEmail,
      sendReminderEmails:
        input.notificationPreferences.automaticallySendReminders,
    },
  };
};
